import React from 'react';
import { DatePicker, Form, Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

interface PopupComponentsProps {
  pickupFrom?: Dayjs | null;
  pickupTo?: Dayjs | null;
  viewFrom?: Dayjs | null;
  viewTo?: Dayjs | null;
  onPickupFromChange: (date: Dayjs | null) => void;
  onPickupToChange: (date: Dayjs | null) => void;
  onViewFromChange: (date: Dayjs | null) => void;
  onViewToChange: (date: Dayjs | null) => void;
  onFinish: () => void;
}

export const PopupComponents: React.FC<PopupComponentsProps> = ({
  pickupFrom = null,
  pickupTo = null,
  viewFrom = null,
  viewTo = null,
  onPickupFromChange,
  onPickupToChange,
  onViewFromChange,
  onViewToChange,
  onFinish
}) => {
  // Constants
  const DEFAULT_PICKUP_HOUR = 6;

  // Styles
  const styles = {
    sectionContainer: {
      border: '1px solid #eee',
      padding: '16px',
      marginBottom: '16px'
    }
  };

  const handlePickupRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates) {
      const [startDate, endDate] = dates;
      if (startDate) {
        // Set time to 6:00:00 local time
        const startWithTime = startDate.hour(DEFAULT_PICKUP_HOUR).minute(0).second(0);
        onPickupFromChange(startWithTime);
      }
      if (endDate) {
        // Set time to 6:00:00 local time
        const endWithTime = endDate.hour(DEFAULT_PICKUP_HOUR).minute(0).second(0);
        onPickupToChange(endWithTime);
      }
    } else {
      // Handle case when date range is cleared
      onPickupFromChange(null);
      onPickupToChange(null);
    }
  };

  const handleViewRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates) {
      const [startDate, endDate] = dates;
      if (startDate) {
        onViewFromChange(startDate);
      }
      if (endDate) {
        onViewToChange(endDate);
      }
    } else {
      // Handle case when date range is cleared
      onViewFromChange(null);
      onViewToChange(null);
    }
  };

  // Create pickup range value for the RangePicker
  const pickupRangeValue: [Dayjs | null, Dayjs | null] | null =
    pickupFrom && pickupTo ? [pickupFrom, pickupTo] : null;

  // Create view range value for the RangePicker
  const viewRangeValue: [Dayjs | null, Dayjs | null] | null =
    viewFrom && viewTo ? [viewFrom, viewTo] : null;

  // Default time values for the time picker
  const defaultTimeValue = dayjs().hour(DEFAULT_PICKUP_HOUR).minute(0).second(0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Form
        layout="vertical"
        onFinish={onFinish}
        style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <div style={styles.sectionContainer}>
          <h4>Pickup range</h4>
          <Form.Item
            name="pickupRange"
            rules={[{ required: true, message: 'Pickup range is required' }]}
          >
            <DatePicker.RangePicker
              value={pickupRangeValue}
              onChange={handlePickupRangeChange}
              format="DD/MM/YYYY HH:mm:ss"
              showTime={{
                defaultValue: [defaultTimeValue, defaultTimeValue],
                format: 'HH:mm:ss'
              }}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </div>

        <div style={styles.sectionContainer}>
          <h4>View range</h4>
          <Form.Item
            name="viewRange"
            rules={[{ required: true, message: 'View range is required' }]}
          >
            <DatePicker.RangePicker
              value={viewRangeValue}
              onChange={handleViewRangeChange}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </div>

        <Form.Item style={{ marginTop: 'auto' }}>
          <Button
            htmlType="submit"
            type="primary"
            style={{ width: '100%' }}
            className="ant-btn-secondary btn-submit"
          >
            Export Excel File
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};
